import { Injectable, Optional } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { TenantService } from 'app/shared/apis/subscription/api/tenant.service';

/**
 * SPEC-web-planner-013 AC-82 (NC-8): the selected client's name for the persona shells
 * (topbar crumb, sidebar footer). Sign-in and activate select the user's compact tenant entry
 * (id, name, roles - no organisation), and an invitee's entry can carry no name, so the AC-52
 * chain (organisation, then the entry's name) can end empty. The last source is one read of the
 * client (subscriptions-api GET /Tenant/{id}), kept here per client id rather than written back
 * to selectedTenant (its setter refreshes the persona). Unknown is null - never 'DS' or 'Workspace'.
 */
@Injectable({ providedIn: 'root' })
export class OrganisationNameService {
    private readonly _fetched = new Map<string, string | null>();
    /** A failed or empty read is asked again this long after it (the pages ask every check)... */
    static readonly RETRY_MS = 30_000;
    /** ...at most this many reads per client in all; after that the name stays unknown until a reload. */
    static readonly MAX_ATTEMPTS = 3;
    private readonly _retryAt = new Map<string, number>();
    private readonly _attempts = new Map<string, number>();
    private readonly _renamed = new Map<string, string>();
    private readonly _changed = new Subject<void>();
    /** Emits when a read resolves with a name, so an OnPush page that showed it as unknown can re-render. */
    readonly changed$ = this._changed.asObservable();

    constructor(
        @Optional() private readonly _auth: AuthService | null,
        @Optional() private readonly _tenants: TenantService | null,
    ) {
        // A held rename must not outlive the session it was made in: a re-selected tenant (it carries the newer
        // organisation) or another user signing in starts from the stored sources again.
        this._auth?.selectedTenant$?.subscribe(() => this._renamed.clear());
        this._auth?.signedOut$?.subscribe(() => this._renamed.clear());
    }

    name(): string | null {
        const tenant: any = this._auth?.selectedTenant;
        const id: string | undefined = tenant?.id;
        if (!id) return null;
        const renamed = this._renamed.get(id);
        if (renamed) return renamed;
        const organisation = tenant.organization?.trim();
        if (organisation) return organisation;
        const entry = (this._auth?.user?.tenants ?? []).find((t: any) => t?.id === id)?.name?.trim();
        if (entry) return entry;
        if (!this._fetched.has(id) && Date.now() >= (this._retryAt.get(id) ?? 0)) this.fetch(id);
        return this._fetched.get(id) ?? null;
    }

    /** SPEC-web-planner-035 AC-3: a rename the Admin just saved. Held here, ahead of every other source, so the crumb and
     *  sidebar show it at once without writing selectedTenant (its setter refreshes the persona and drops the seat reads). */
    rename(id: string, name: string): void {
        const n = name?.trim();
        if (!id || !n) return;
        this._renamed.set(id, n);
        this._changed.next();
    }

    /** One read at a time per client. GET /Tenant/{id} answers a missing client with 204, a refusal with 400
     *  or 403 (a 403 can be brief right after activation), and a client with no organisation with an empty 200;
     *  none of these is told apart as final, so each is asked again after RETRY_MS, up to MAX_ATTEMPTS reads,
     *  and then left unknown - no endless polling, and one bad read does not blank the name. */
    private fetch(id: string): void {
        this._fetched.set(id, null);
        if (!this._tenants) return;
        const attempts = (this._attempts.get(id) ?? 0) + 1;
        this._attempts.set(id, attempts);
        const retryLater = () => {
            if (attempts >= OrganisationNameService.MAX_ATTEMPTS) return;   // final: stays null
            this._fetched.delete(id);
            this._retryAt.set(id, Date.now() + OrganisationNameService.RETRY_MS);
        };
        this._tenants.tenantIdGet(id).subscribe({
            next: (resp: any) => {
                const organisation = resp?.isSuccess !== false ? resp?.result?.organization?.trim() : '';
                if (organisation) { this._fetched.set(id, organisation); this._changed.next(); } else retryLater();
            },
            error: () => retryLater(),
        });
    }
}
